"use client";
import React, { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";

interface Picture {
  data: {
    id: string;
    attributes: {
      name: string;
      alternativeText: string;
      url: string;
    };
  };
}

interface BrandLogo {
  screenDisplay: boolean;
  href: string;
  image: Picture;
}

interface BrandsLogoSliderProps {
  data: {
    title: string;
    brandLogo: BrandLogo[];
    isLive: boolean;
    useH1Tag?: boolean;
  };
}

export const BrandsLogoSlider = ({ data }: BrandsLogoSliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4); // Default number of logos to show
  const [windowWidth, setWindowWidth] = useState(0);

  // Filter logos to display and repeat them 4 times
  const baseLogos = data.brandLogo.filter((logo) => logo.screenDisplay);
  const visibleLogos = Array(10).fill(baseLogos).flat(); 

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth < 640) {
        setVisibleCount(1); // Mobile - show 1 logo
      } else if (window.innerWidth < 768) {
        setVisibleCount(2); // Small tablet - show 2 logos
      } else if (window.innerWidth < 1024) {
        setVisibleCount(3); // Tablet - show 3 logos
      } else {
        setVisibleCount(4); // Desktop - show 4 logos
      }
    };

    // Set initial width
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-advance the slider every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex + 1 >= visibleLogos.length ? 0 : prevIndex + 1
      );
    }, 3500);

    return () => clearInterval(interval);
  }, [visibleLogos.length, windowWidth]); // Add windowWidth to dependencies

  // Get the current set of logos to display
  const getVisibleLogos = useCallback(() => {
    const endIndex = currentIndex + visibleCount;
    if (endIndex <= visibleLogos.length) {
      return visibleLogos.slice(currentIndex, endIndex);
    } else {
      return [
        ...visibleLogos.slice(currentIndex),
        ...visibleLogos.slice(0, endIndex - visibleLogos.length),
      ];
    }
  }, [currentIndex, visibleCount, visibleLogos]);

  // Calculate slide width based on visible count
  const slideWidth = 100 / visibleCount;

  const pathName = usePathname();
  const isLounge = (pathName !== "/cafe-te-amo");

  return (
    data.isLive && (
      <div className="relative py-8 md:py-16 bg-white tracking-normal">
        {/* Hard-coded section subtitle */}
        {
          isLounge &&
          <p className="flex justify-center text-sm md:text-md font-medium text-th-primary">
            WELCOME TO OUR PHOTO GALLERY
          </p>
        }

        {
          !isLounge &&
          <p className="flex justify-center text-lg font-medium text-th-primary">
            Café Te Amo
          </p>
        }

        {/* Title */}
        {data?.useH1Tag ? (
          <h1 className="relative z-10 text-2xl md:text-3xl lg:text-4xl xl:text-5xl my-4 text-center text-black font-bold font-arial">
            {data.title}
          </h1>
        ) : (
          <h2 className="relative z-10 text-2xl md:text-3xl lg:text-4xl xl:text-5xl my-4 text-center text-black font-bold font-arial">
            {data.title}
          </h2>
        )}

        <div className="flex justify-center">
          <Image
            src={`/assets/images/icon-hero2.svg`}
            alt="Decorative Hero Icon"
            width={200}
            height={200}
            className="mx-auto md:mx-0 mb-4 md:mb-6 w-[20%] md:w-[10%]"
          />
        </div>

        {/* Slider Container */}
        <div className="relative m-auto max-w-[90%] h-auto md:h-[40%] w-full overflow-hidden px-2 md:px-4">
          {/* Slider Track */}
          <div
            className="flex transition-transform duration-1000 ease-in-out"
            style={{
              transform: `translateX(-${currentIndex * slideWidth}%)`,
            }}
            aria-live="polite"
          >
            {visibleLogos.map((logo, index) => (
              <div
                className={`slide flex w-[${slideWidth}%] h-[200px] md:h-[200px] lg:h-[240px] items-center justify-center px-2 md:px-4 shrink-0`}
                key={`${index}-${logo.href}`}
                style={{ width: `${slideWidth}%` }} // Inline style for dynamic width
              >
                <Link
                  href={logo.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-full flex items-center justify-center"
                >
                  <div className="flex items-center justify-center w-[400px] h-[500px]  bg-white overflow-hidden">
                    <Image
                      src={getStrapiMedia(logo.image.data.attributes.url) || ""}
                      alt={logo.image.data.attributes.alternativeText || "Brand Logo"}
                      width={400} // ✅ Set fixed dimensions instead of fill
                      height={500}
                      quality={100}
                      className="object-cover transition-transform duration-300"
                      priority={index < visibleCount}
                    />
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}

        <div className="flex items-end justify-center mt-4 md:mt-8 sm-my-8">
          <button className="px-4 py-2 md:px-6 md:py-3 text-sm md:text-lg font-bold border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white transition-all duration-300 whitespace-nowrap">
            {isLounge && "Discover More"}
            {!isLounge && "Call Now"}
          </button>
        </div>
      </div>
    )
  );
};